var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { concatStyleSets, DirectionalHint, Icon, Link, Modal, Stack, Text, useTheme } from "@fluentui/react";
import React, { useEffect, useId, useRef, useState } from "react";
import { tryGetHostApi } from "../../../app/externalApi/externalApi";
import { isFeatureEnabled } from "../../../app/externalApi/VelocityApi";
import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { useCtaSettingContext } from "../../contexts/CtaSettingContext";
import { getSettings, updateSetting } from "../../externalApi/AepApi";
import { getDefaultAccountType } from "../../externalApi/AuthenticatorApi";
import { setLaunchAttributionContext } from "../../externalApi/DashboardHostApi";
import { isTextDirectionRTL } from "../../externalApi/DashboardHostApiInternal";
import { getResources } from "../../getResources";
import { initializeWidgetIcons } from "../../initializeWidgetIcons";
import { tryParseJson } from "../../utils/stringUtilities";
import { SettingsInteraction } from "../../utils/widgetDialogUtilities";
import { IconButton } from "../generic/IconButton/IconButton";
import { ScrollableStack } from "../generic/ScrollableStack/ScrollableStack";
import { getBaseDialogModalStyle, getBreadCrumbTitleStyleStacks, getDisplayFlex, getModalCloseButtonIconStyle, getModalCloseButtonStyle, getRTLModalCloseButtonStyle, getSettingsScrollableAreaStyle, getTitleChevronStyles, getTitleSectionStyles, getTitleStackStyle, getTitleStyles } from "./EvolvedSettingsDialog.styles";
import EvolvedSettingsMainPage from "./EvolvedSettingsMainPage";
import EvolvedSettingsNotificationsPage from "./EvolvedSettingsNotificationsPage";
import EvolvedSettingsPersonalizePage from "./EvolvedSettingsPersonalizePage";
import EvolvedSettingsShowOrHidePage from "./EvolvedSettingsShowOrHidePage";
const EvolvedSettingsPage = (({ title, content }) => {
    return (React.createElement("div", { role: "dialog", "aria-labelledby": "modal-title" },
        React.createElement(Stack.Item, { styles: getTitleSectionStyles(), id: "modal-title", role: "heading", tabIndex: -1 }, title),
        React.createElement(ScrollableStack, { styles: getSettingsScrollableAreaStyle(), "aria-labelledby": "modal-title" }, content)));
});
const EvolvedSettingsTitle = (({ title, subTitle, onClick, }) => {
    const theme = useTheme();
    const id = "title-" + useId();
    return (React.createElement(Text, { id: id, styles: getTitleStyles(theme), as: "h1", role: "heading" },
        React.createElement(Stack, { styles: getDisplayFlex(getTitleStackStyle()), horizontal: true },
            React.createElement(Link, { onClick: onClick, styles: getBreadCrumbTitleStyleStacks(theme), underline: false }, title),
            React.createElement(Icon, { styles: getTitleChevronStyles(theme), iconName: "ChevronRight" }),
            subTitle)));
});
const EvolvedSettingsPersonalizeTitle = (({ title, subTitle, }) => {
    const theme = useTheme();
    const id = "title-" + useId();
    return (React.createElement(Text, { id: id, styles: getTitleStyles(theme), as: "h1", role: "heading" },
        React.createElement(Stack, { styles: getDisplayFlex(getTitleStackStyle()), horizontal: true },
            title,
            subTitle)));
});
// Modal Window for displaying the various post DMA/3P Feeds Settings pages in their L1/L2 layouts.
const EvolvedSettingsDialog = React.memo((_a) => {
    var _b, _c, _d;
    var { isOpen, onDismiss, showPickerDialog, settingsPageSettings, setEvolvedSettingsPageSettings, personalizeToShow } = _a, rest = __rest(_a, ["isOpen", "onDismiss", "showPickerDialog", "settingsPageSettings", "setEvolvedSettingsPageSettings", "personalizeToShow"]);
    const theme = useTheme();
    const ctaSettingContext = useCtaSettingContext();
    const urlParams = new URLSearchParams(window.location.search);
    const region = ((_b = urlParams.get("region")) === null || _b === void 0 ? void 0 : _b.toUpperCase()) || undefined;
    const modalRef = useRef(null);
    const [isBadgingEnabled, setBadgingEnabled] = useState(false);
    const [isRotationEnabled, setRotationEnabled] = useState(false);
    const [isHoverEnabled, setHoverEnabled] = useState(false);
    const [accountType, setAccountType] = useState("");
    const [whatIsShown, setWhatIsShown] = useState({ mainSettings: true });
    const [isRotationEnabledByPolicy, setIsRotationEnabledByPolicy] = useState(true);
    const [isBadgingEnabledByPolicy, setBadingIsEnabledByPolicy] = useState(true);
    const [isFirstPartyExperienceOptimizationEnabled, setFirstPartyExperienceOptimizationEnabled] = useState(false);
    const isRTL = isTextDirectionRTL();
    const dmaPolicyApi = (_c = window.__com_microsoft_dsh_widgetModelApiFactory) === null || _c === void 0 ? void 0 : _c.tryGetApi("dmapolicyevaluator", 1, 1);
    const [feedProviderModelRegistrations, setFeedProviderModelRegistrations] = useState();
    const [feedModelRegistrations, setFeedModelRegistrations] = useState();
    const [personalizationFeedProvider, setPersonalizationFeedProvider] = useState();
    const is3pPlatformFeedsEnabled = isFeatureEnabled("Feature_3pPlatformFeeds");
    const is3pAEPEnabled = isFeatureEnabled("Feature_3PAEP");
    const isBoardsLiteEnabled = isFeatureEnabled("Feature_BoardsLite");
    const isDisable1PNotificationsOnDMADevices = isFeatureEnabled("Feature_Disable1PNotificationsOnDMADevices");
    const feedModelApi = (_d = window.__com_microsoft_dsh_widgetModelApiFactory) === null || _d === void 0 ? void 0 : _d.tryGetApi("feedsmodel", 1, 1);
    const [isFeedProviderApiV3, setIsFeedProviderApiV3] = useState(false);
    initializeWidgetIcons(); // Initializes the Widget Icons
    async function onSettingsShown() {
        getSettings().then((hostSettingsJson) => {
            const settingsJSON = tryParseJson(hostSettingsJson);
            // Json is in the format of: { Settings: [{BadgingEnabled: val, ...}, {BadgingEnabled: val, ...}]}
            // meaning while unlikely, it's possible for us to receive multiple definitions in the array, so iterate
            // through each.
            if (!!settingsJSON && !!settingsJSON.Settings) {
                for (const setting in settingsJSON.Settings) {
                    if ("BadgingEnabled" in settingsJSON.Settings[setting]) {
                        setBadgingEnabled(settingsJSON.Settings[setting].BadgingEnabled);
                    }
                    else if ("HoverEnabled" in settingsJSON.Settings[setting]) {
                        setHoverEnabled(settingsJSON.Settings[setting].HoverEnabled);
                    }
                    else if ("RotationEnabled" in settingsJSON.Settings[setting]) {
                        setRotationEnabled(settingsJSON.Settings[setting].RotationEnabled);
                    }
                }
            }
            else {
                setBadgingEnabled(true);
                setHoverEnabled(true);
                setRotationEnabled(true);
            }
        });
        if (dmaPolicyApi) {
            dmaPolicyApi.isWidgetsTaskbarAnnouncementsEnabled().then((isAepAnnouncementsEnabled) => {
                // Non-Aep Rotations is disabled in this case.
                setIsRotationEnabledByPolicy(isAepAnnouncementsEnabled);
            });
            dmaPolicyApi.isWidgetsTaskbarBadgesEnabled().then((isAepBadgingEnabled) => {
                // Non-Aep Badging is disabled in this case.
                setBadingIsEnabledByPolicy(isAepBadgingEnabled);
            });
            dmaPolicyApi.isWidgetsFirstPartyExperienceOptimizationEnabled().then((isOptimizationEnabled) => {
                // Footer in Settings can mention Powered by MSN.
                setFirstPartyExperienceOptimizationEnabled(isOptimizationEnabled);
            });
        }
        setIsFeedProviderApiV3(!!await tryGetHostApi("FeedProvider", 2, 2));
        setFeedProviderModelRegistrations(feedModelApi.getFeedProviders().sort((provider1, provider2) => provider1.displayName.localeCompare(provider2.displayName)));
    }
    async function onLinkInvoked(href, interaction) {
        let settingsActionPayload = {
            "settingsItem": {
                "interactionType": interaction,
                "isPeregrine": false
            }
        };
        await setLaunchAttributionContext(JSON.stringify(settingsActionPayload));
        window.location.href = href;
        settingsInteracted(interaction, href);
    }
    async function settingsInteracted(interaction, href = "") {
        Measures.logEvent(dashboard, "NewSettingsDialogInteracted", 33554432 /* productAndServiceUsage */, {
            change: interaction,
            isDynamicFeedEnabled: true,
            link: href,
        });
    }
    async function settingsToggleInteracted(setting, state) {
        Measures.logEvent(dashboard, "SettingToggleInteracted", 33554432 /* productAndServiceUsage */, {
            setting: setting,
            state: state,
        });
    }
    function onBadgingInteracted() {
        let value = !isBadgingEnabled;
        updateSetting({ BadgingEnabled: value });
        settingsToggleInteracted("badgingEnabled", value);
        setBadgingEnabled(value);
    }
    function onHoverInteracted() {
        let value = !isHoverEnabled;
        updateSetting({ HoverEnabled: value });
        settingsToggleInteracted("hoverEnabled", value);
        setHoverEnabled(value);
    }
    function onRotationInteracted() {
        let value = !isRotationEnabled;
        updateSetting({ RotationEnabled: value });
        settingsToggleInteracted("rotationEnabled", value);
        setRotationEnabled(value);
    }
    function onPersonalizeInteracted() {
        let newSettings = Object.assign(Object.assign({}, settingsPageSettings), { isPersonalizeExpanded: !settingsPageSettings.isPersonalizeExpanded });
        setEvolvedSettingsPageSettings(newSettings);
    }
    async function launchStoreToFeeds() {
        onLinkInvoked("ms-windows-store://collection/?collectionid=MerchandiserContent/Apps/WinOS/FeedProviderApps/_FeedProviderApps", SettingsInteraction.LaunchFeedStorePage);
    }
    function onAboutInteracted() {
        let newSettings = Object.assign(Object.assign({}, settingsPageSettings), { isAboutExpanded: !settingsPageSettings.isAboutExpanded });
        setEvolvedSettingsPageSettings(newSettings);
    }
    function showMainPage() {
        setWhatIsShown({ mainSettings: true });
    }
    function showShowOrHide() {
        setWhatIsShown({ showOrHide: true });
    }
    function showPersonalization() {
        setWhatIsShown({ personalize: true });
    }
    function showNotifications() {
        setWhatIsShown({ notifications: true });
        if (is3pAEPEnabled && !!isFeedProviderApiV3) {
            setFeedModelRegistrations(feedModelApi.getEnabledFeedOrderByProvider());
        }
    }
    useEffect(() => {
        try {
            if (isOpen) {
                setWhatIsShown({ mainSettings: true });
                onSettingsShown();
                if (personalizeToShow.length > 0) {
                    let provider = feedModelApi.getFeedProviderRegistration(personalizeToShow);
                    if (provider) {
                        setPersonalizationFeedProvider(provider);
                        setWhatIsShown({ personalize: true });
                    }
                }
            }
        }
        catch (error) {
            Trace.logEvent(dashboard, "EvolvedSettingsShownOrHiddenError", 3 /* error */, {
                error: error,
            });
        }
        Measures.logEvent(dashboard, "DashboardSettingsDialogShownOrHidden", 16777216 /* productAndServicePerformance */, {
            isInCtaRegion: ctaSettingContext.isInCtaRegion,
            isMeteredConnectionAllowed: ctaSettingContext.isMeteredConnectionUsageAllowed,
            isShown: isOpen,
            region: region,
        });
    }, [isOpen]);
    useEffect(() => {
        var _a;
        (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [whatIsShown]);
    useEffect(() => {
        getDefaultAccountType().then((value) => {
            setAccountType(value);
        });
    }, []);
    let closeButton = (React.createElement(IconButton, { "aria-label": getResources().Dialog_CloseButtonDescription, id: "close-button" + useId(), onClick: onDismiss, styles: isRTL ? getRTLModalCloseButtonStyle(theme) : getModalCloseButtonStyle(theme), tooltipText: getResources().Dialog_CloseButtonDescription, tooltipDirectionalHint: DirectionalHint.topRightEdge, tooltipDirectionalHintForRTL: DirectionalHint.topLeftEdge },
        React.createElement(Icon, { iconName: "SegoeFluentChromeClose", styles: getModalCloseButtonIconStyle() })));
    return (React.createElement(Modal, Object.assign({}, rest, { componentRef: modalRef, isOpen: isOpen, onDismiss: onDismiss, overlay: { styles: { root: { borderRadius: "6px" } } }, styles: concatStyleSets(getBaseDialogModalStyle(theme)), isBlocking: true, allowTouchBodyScroll: true }),
        whatIsShown.mainSettings &&
            React.createElement(EvolvedSettingsPage, { title: React.createElement(Text, { styles: getTitleStyles(theme) }, getResources().SettingsEvolvedDialog_SettingsTitleText), content: React.createElement(EvolvedSettingsMainPage, { onDismiss: onDismiss, accountType: accountType, showPickerDialog: showPickerDialog, isPersonalizeExpanded: settingsPageSettings.isPersonalizeExpanded, onPersonalizeInteracted: onPersonalizeInteracted, isHoverEnabled: isHoverEnabled, onHoverInteracted: onHoverInteracted, isNotificationsEnabledByPolicy: isRotationEnabledByPolicy || isBadgingEnabledByPolicy || is3pAEPEnabled, onLinkInvoked: onLinkInvoked, settingsInteracted: settingsInteracted, settingsToggleInteracted: settingsToggleInteracted, isAboutExpanded: settingsPageSettings.isAboutExpanded, onAboutInteracted: onAboutInteracted, showNotifications: showNotifications, showShowOrHide: showShowOrHide, showPersonalize: showPersonalization, launchStoreToFeeds: launchStoreToFeeds, feedProviderModelRegistrations: feedProviderModelRegistrations, setPersonalizationFeedProvider: setPersonalizationFeedProvider, isFirstPartyExperienceOptimizationEnabled: isFirstPartyExperienceOptimizationEnabled }) }),
        whatIsShown.notifications &&
            React.createElement(EvolvedSettingsPage, { title: React.createElement(EvolvedSettingsTitle, { title: getResources().SettingsEvolvedDialog_SettingsTitleText, subTitle: getResources().SettingsEvolvedDialog_Notifications_NotificationsText, onClick: showMainPage }), content: React.createElement(EvolvedSettingsNotificationsPage, { isBoardsLiteEnabled: isBoardsLiteEnabled, is3pAEPEnabled: is3pAEPEnabled, isDisable1PNotificationsOnDMADevices: isDisable1PNotificationsOnDMADevices, isFeedProviderApiV3: isFeedProviderApiV3, feedProviderModelRegistrations: feedProviderModelRegistrations, feedModelRegistrations: feedModelRegistrations, showShowOrHide: showShowOrHide, isBadgingEnabledByPolicy: isBadgingEnabledByPolicy, isBadgingEnabled: isBadgingEnabled, onBadgingInteracted: onBadgingInteracted, isRotationEnabledByPolicy: isRotationEnabledByPolicy, isRotationEnabled: isRotationEnabled, onRotationInteracted: onRotationInteracted, showMainSettings: showMainPage, feedModelApi: feedModelApi }) }),
        whatIsShown.personalize &&
            React.createElement(EvolvedSettingsPage, { title: React.createElement(EvolvedSettingsPersonalizeTitle, { title: getResources().SettingsEvolvedDialog_Personalization_PersonalizeYourContentText + " ", subTitle: personalizationFeedProvider ? personalizationFeedProvider === null || personalizationFeedProvider === void 0 ? void 0 : personalizationFeedProvider.displayName : "" }), content: React.createElement(EvolvedSettingsPersonalizePage, { personalizationFeedProvider: personalizationFeedProvider }) }),
        whatIsShown.showOrHide &&
            React.createElement(EvolvedSettingsPage, { title: React.createElement(EvolvedSettingsTitle, { title: getResources().SettingsEvolvedDialog_SettingsTitleText, subTitle: isBoardsLiteEnabled
                        ? getResources().SettingsEvolvedDialog_ShowBoards_ShowOrHideBoardsText
                        : getResources().SettingsEvolvedDialog_ShowFeeds_ShowOrHideFeedsText, onClick: showMainPage }), content: React.createElement(EvolvedSettingsShowOrHidePage, { is3pPlatformFeedsEnabled: is3pPlatformFeedsEnabled, isBoardsLiteEnabled: isBoardsLiteEnabled, onLinkInvoked: onLinkInvoked, feedProviderModelRegistrations: feedProviderModelRegistrations, feedModelApi: feedModelApi, launchStoreToFeeds: launchStoreToFeeds }) }),
        closeButton));
});
export default EvolvedSettingsDialog;
